import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, X } from "lucide-react";

/**
 * Floating question-mark icon that, when clicked, reveals an informational modal.
 * Used to introduce foundational lore (Who is Primordeia? / SFRH means...).
 *
 * Props:
 *   title    — modal heading
 *   children — modal body (string or JSX). String body is auto-split into paragraphs.
 *   image    — optional image URL shown above the body
 *   testId   — root data-testid prefix
 *   className/style — positioning passed through to the button wrapper
 */
const InfoModal = ({ title, children, image, testId = "info", className = "", style = {} }) => {
  const [open, setOpen] = useState(false);

  // Auto-split string children into paragraphs at blank lines or curly-quote starts.
  // Use a content-derived key (first 20 chars + index) so React stays stable across re-renders.
  const bodyContent = typeof children === "string"
    ? children
        .split(/\n\s*\n|(?=\u201C)/g)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((p, i) => (
          <p key={`p-${i}-${p.slice(0, 20).replace(/\s+/g, "_")}`}
            className="mb-3 last:mb-0 text-sm leading-relaxed"
            style={{ color: "#E8E0F8", fontFamily: "'Nunito', sans-serif" }}>
            {p}
          </p>
        ))
    : children;

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        whileHover={{ scale: 1.15, rotate: 8 }}
        whileTap={{ scale: 0.92 }}
        animate={{
          y: [0, -3, 0],
          boxShadow: [
            "0 4px 12px rgba(79,55,139,0.35)",
            "0 6px 18px rgba(212,165,116,0.55)",
            "0 4px 12px rgba(79,55,139,0.35)",
          ],
        }}
        transition={{ y: { repeat: Infinity, duration: 2.4, ease: "easeInOut" }, boxShadow: { repeat: Infinity, duration: 2.4, ease: "easeInOut" } }}
        className={`w-7 h-7 rounded-full flex items-center justify-center ${className}`}
        style={{
          background: "linear-gradient(135deg, #4F378B 0%, #D4A574 100%)",
          color: "#FFF",
          border: "1.5px solid rgba(255,248,220,0.6)",
          ...style,
        }}
        aria-label={`Open info: ${title}`}
        data-testid={`${testId}-trigger`}
      >
        <HelpCircle size={14} strokeWidth={2.5} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[400] flex items-center justify-center px-4"
            style={{ background: "rgba(14, 8, 24, 0.82)", backdropFilter: "blur(10px)" }}
            data-testid={`${testId}-modal`}
          >
            <motion.div
              initial={{ scale: 0.88, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 18, stiffness: 220 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full rounded-3xl p-7"
              style={{
                background: "linear-gradient(135deg, #1A1030 0%, #2D1F50 55%, #1A2810 100%)",
                boxShadow: "0 40px 120px rgba(0,0,0,0.7), 0 0 80px rgba(212,165,116,0.3)",
                border: "1px solid rgba(212,165,116,0.4)",
              }}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition"
                style={{ color: "#D4A574" }}
                aria-label="Close"
                data-testid={`${testId}-close`}
              >
                <X size={18} />
              </button>

              {image && (
                <div className="flex justify-center mb-4">
                  <img src={image} alt={title}
                    className="w-32 h-32 rounded-2xl object-cover"
                    style={{ border: "1px solid rgba(212,165,116,0.4)", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}
                  />
                </div>
              )}

              <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2 text-center"
                style={{ color: "#D4A574", fontFamily: "'Cinzel', serif" }}>
                Whispered teaching
              </div>
              <h3 className="text-xl font-bold mb-4 text-center"
                style={{ color: "#FFF", fontFamily: "'Fredoka', sans-serif" }}
                data-testid={`${testId}-title`}>
                {title}
              </h3>

              <div className="max-h-[55vh] overflow-y-auto pr-1" data-testid={`${testId}-body`}>
                {bodyContent}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default InfoModal;
